import type { MediaUrl, TvChannel, TvChannelWithEpg } from '@setplex/tria-api'
import type { TOnGoLive } from '~/features/rewind/useRewindCallbacks'
import type { CurrentProgramMediaUrl } from '~/shared/interfaces'
import type { ViewsUnionType } from '~/widgets/Player/hooks/PlayerOrderedViews'

import { alternativePlayer } from '@setplex/pureya'
import { ScheduleType, VideoType } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback, useMemo, useState } from 'react'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { isPaid } from '~/features/payment'
import {
  usePinLockPlayerPaymentPopup,
  usePinLockPlayerPopup,
} from '~/features/pin-lock'
import { recentlyWatchedModel } from '~/features/recently-watched'
import { useGoLiveOnError } from '~/features/rewind/useGoLiveOnError'
import { usePauseControl } from '~/features/rewind/usePauseContorl'
import { usePlayerProgress } from '~/features/rewind/usePlayerProgress'
import { useRewindCallbacks } from '~/features/rewind/useRewindCallbacks'
import { useRewindState } from '~/features/rewind/useRewindState'
import { epgRoutePopUpModel } from '~/routes/epg/root/model/popups'
import { CONTENT_TYPE } from '~/shared/constants/content'
import { getProgramTitle } from '~/shared/formatters/tv'
import { noop } from '~/shared/helpers'
import { useResetToken } from '~/shared/tools/player'
import { PlayerEmptyPoster } from '~/shared/ui/components/player'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
import {
  $currentEpgInfo,
  $playerProgramUrlSyncedInfo,
  fetchRewindUrl,
  fetchUrl as fetchUrlEvent,
  updateChannelEpg as updateChannelEpgEvent,
} from '~/widgets/epg/player/model'
import { PlayerPaymentPopup } from '~/widgets/Payment'
import { Geoblock, usePlayer } from '~/widgets/Player'
import { PlayerOrderedViews } from '~/widgets/Player/hooks/PlayerOrderedViews'
import { PlayerGoLivePopup } from '~/widgets/Player/PlayerGoLivePopup'
import { PlayerSignInToWatchPopup } from '~/widgets/Player/PlayerSignInToWatchPopup'
import { PlayerWrap } from '~/widgets/Player/PlayerWrap'
import * as model from '../../model'
import * as epg from '../../model/epg'
import { ProgramView, useProgramViewState } from './ProgramView'

export function PlayerWindow() {
  const player = usePlayer()

  const [isFullscreen, setFullscreenChange] = useState(false)

  const selectedEpgChannel: TvChannel | null = useUnit(model.$epgActiveChannel)
  const { liveRewind, id, logoUrl, name, formattedName } =
    selectedEpgChannel ?? {}

  const contentKey = id

  const addCurrentChannelToRW = useUnit(recentlyWatchedModel.addTvChannelLocal)

  const [safeToShow, setSafeToShow] = useState(false)
  const [activeView, setActiveView] = useState<ViewsUnionType | undefined>(
    undefined
  )

  const title = formattedName ?? name ?? ''
  const pinLocked = useUnit(selectEpgChannelModel.$isPinLocked)
  const showPaymentPopupInPlayer = useUnit(epgRoutePopUpModel.$showPaymentPopup)
  const pinApi = usePinLockPlayerPopup(title, selectedEpgChannel, pinLocked)

  const { PINLockPlayerPopupComponent, closePinLockPopup } = pinApi

  const pinApiPayment = usePinLockPlayerPaymentPopup(
    pinLocked,
    showPaymentPopupInPlayer
  )
  const { PINLockPlayerPaymentPopupComponent, showPinlockPlayerPaymentPopup } =
    pinApiPayment

  const clickChannel = useUnit(epgChannelsWidgetModel.probe)

  const activeProgram = useUnit($currentEpgInfo)

  const currentUrlSyncedInfo: CurrentProgramMediaUrl | null = useUnit(
    $playerProgramUrlSyncedInfo
  )

  const currentPlayerPageUrl: MediaUrl | null = currentUrlSyncedInfo
    ? {
        playbackUrl: currentUrlSyncedInfo.playbackUrl,
        drm: currentUrlSyncedInfo.drm,
        stoppedTime: currentUrlSyncedInfo.stoppedTime,
        liveRewindOptions: currentUrlSyncedInfo.liveRewindOptions,
      }
    : null

  const activeChannelList: TvChannelWithEpg[] = useUnit(epg.$channels)

  const showSignInToWatchPopupInPlayer = useUnit(
    epgRoutePopUpModel.$showSignInToWatchPopup
  )
  const signInToWatchPopupSubmit = useUnit(
    epgRoutePopUpModel.signInToWatchPopupSubmit
  )

  const proceedPurchaseClick = useUnit(epgRoutePopUpModel.proceedPurchaseClick)
  const reloadTvPlayerUrl = useUnit(model.reloadEpgTvPlayerUrl)
  const { onPlaying, onTokenExpired } = useResetToken(reloadTvPlayerUrl)

  const playListInfo = useMemo(() => {
    const channelsCount = activeChannelList.length
    if (!channelsCount) return

    const currentIndex = activeChannelList.findIndex(
      (channel) => channel.id === selectedEpgChannel?.id
    )
    const isFirstIndex = currentIndex === 0
    const isLastIndex = currentIndex === channelsCount - 1

    const previousIndex = isFirstIndex ? channelsCount - 1 : currentIndex - 1
    const nextIndex = isLastIndex ? 0 : currentIndex + 1

    const nextChannel = activeChannelList[nextIndex]
    const previousChannel = activeChannelList[previousIndex]

    return {
      previousChannel,
      nextChannel,
    }
  }, [selectedEpgChannel, activeChannelList])

  const isChannelsFilled = activeChannelList.length > 1

  const updateSelectedChannelId = useUnit(selectEpgChannelModel.setActiveId)

  const handlePlay = useCallback(
    (channelId?: number | null) => {
      if (!channelId) return

      clickChannel(channelId)
      updateSelectedChannelId(channelId)
    },
    [updateSelectedChannelId, clickChannel]
  )

  const onPlayPrevious = useCallback(() => {
    const previousChannel = playListInfo?.previousChannel
    handlePlay(previousChannel?.id)
  }, [handlePlay, playListInfo?.previousChannel])

  const onPlayNext = useCallback(() => {
    const nextChannel = playListInfo?.nextChannel
    handlePlay(nextChannel?.id)
  }, [handlePlay, playListInfo?.nextChannel])

  const contentId = selectedEpgChannel?.id ? String(selectedEpgChannel.id) : ''

  const subtitle = getProgramTitle({
    startTimeMs: activeProgram?.actualStartMs,
    programTitle: activeProgram?.title,
    endTimeMs: activeProgram?.actualStopMs,
  })

  const npawData = useUnit(epg.$playerNpawData)

  const onCloseClick = useCallback(() => {
    if (document.fullscreenElement) {
      return document.exitFullscreen()
    }
    // @ts-ignore: TS2551 because this fix only Safari
    if (document.webkitFullscreenElement) {
      // @ts-ignore: TS2551 because this fix only Safari
      return document.webkitExitFullscreen()
    }
  }, [])

  const isEnabledLiveRewind =
    Boolean(currentPlayerPageUrl?.playbackUrl) &&
    Boolean(liveRewind) &&
    Boolean(contentKey)

  const rewindState = useRewindState({
    enabled: isEnabledLiveRewind,
    mediaUrlData: currentUrlSyncedInfo,
    contentId: id,
    contentType: VideoType.CHANNEL,
  })
  const { isRewound, rewindDetails, isRewindDelayed } = rewindState

  const liveProgressBar = usePlayerProgress({
    enabled: isEnabledLiveRewind,
    mediaUrlData: currentUrlSyncedInfo,
  })

  const fetchRewoundUrl = useUnit(fetchRewindUrl)
  const fetchUrl = useUnit(fetchUrlEvent)

  const {
    onLiveRewind,
    onGoLive: onGoLiveOriginal,
    isRewindPending,
  } = useRewindCallbacks({
    enabled: isEnabledLiveRewind,
    mediaUrlData: currentUrlSyncedInfo,
    contentKey,
    liveProgressBar,
    rewindState,
    fetchRewoundUrl,
    fetchUrl,
    player,
  })

  const { onDelayedLiveRewind } = usePauseControl({
    enabled: isEnabledLiveRewind,
    liveProgressBar,
    rewindState,
    onLiveRewind,
    player,
  })

  const { showGoLivePopup, handleGoLive: handleGoLivePopup } = useGoLiveOnError(
    {
      enabled: isEnabledLiveRewind,
      player,
      rewindState,
      contentKey,
    }
  )

  const onGoLive: TOnGoLive = useCallback(
    (event) => {
      handleGoLivePopup()
      onGoLiveOriginal(event)
    },
    [handleGoLivePopup, onGoLiveOriginal]
  )

  const updateChannelEpg = useUnit(updateChannelEpgEvent)

  const onProgressReachedEnd = useCallback(() => {
    const hasEpg = currentUrlSyncedInfo?.scheduleType === ScheduleType.EPG
    if (!hasEpg) return

    updateChannelEpg()
  }, [currentUrlSyncedInfo?.scheduleType, updateChannelEpg])

  const { onCloseProgram, onProgramClick } = useProgramViewState({ safeToShow })

  const views = useMemo(
    () => ({
      geoLock: <Geoblock smallPlayer isFullscreen={isFullscreen} />,
      signInToWatch: (
        <PlayerSignInToWatchPopup
          onSubmit={signInToWatchPopupSubmit}
          smallPlayer
          isFullscreen={isFullscreen}
        />
      ),
      pinLock: isFullscreen ? <PINLockPlayerPopupComponent /> : <></>, // page popup is used fo small player
      pinLockPayment: isFullscreen ? (
        <PINLockPlayerPaymentPopupComponent />
      ) : (
        <></>
      ),
      payment: (
        <PlayerPaymentPopup
          onSubmit={proceedPurchaseClick}
          smallPlayer
          isFullscreen={isFullscreen}
        />
      ),
      goLive: (
        <PlayerGoLivePopup
          onSubmit={onGoLive}
          smallPlayer
          isFullscreen={isFullscreen}
        />
      ),
      program: <ProgramView onCloseClick={onCloseProgram} />,
    }),
    [
      PINLockPlayerPopupComponent,
      PINLockPlayerPaymentPopupComponent,
      isFullscreen,
      onCloseProgram,
      signInToWatchPopupSubmit,
      proceedPurchaseClick,
      onGoLive,
    ]
  )

  player.events.on?.('loadeddata', () => {
    if (selectedEpgChannel) addCurrentChannelToRW(selectedEpgChannel)
  })

  player.events.on?.('fullscreenchange', () => {
    if (player.instance?.isFullscreen()) {
      setFullscreenChange(true)
    } else {
      setFullscreenChange(false)

      if (pinLocked) {
        closePinLockPopup()
      }
    }
  })

  const playerChildren = useMemo(
    () => (
      <PlayerOrderedViews
        views={views}
        onSafeChanged={setSafeToShow}
        onViewChanged={setActiveView}
        content={selectedEpgChannel}
        pinLocked={pinLocked}
        pinLockPayment={showPinlockPlayerPaymentPopup}
        showSignInToWatchPopup={showSignInToWatchPopupInPlayer}
        showPaymentPopup={showPaymentPopupInPlayer}
        showGoLivePopup={showGoLivePopup}
        player={player}
      />
    ),
    [
      pinLocked,
      selectedEpgChannel,
      showGoLivePopup,
      showPaymentPopupInPlayer,
      showSignInToWatchPopupInPlayer,
      views,
      player,
      showPinlockPlayerPaymentPopup,
    ]
  )

  const className = !isFullscreen && pinLocked ? 'vjs-hidden-controls' : ''

  return (
    <PlayerWrap
      player={player}
      miniPlayer
      using={alternativePlayer}
      mediaUrlData={currentPlayerPageUrl}
      contentType={CONTENT_TYPE.TV}
      contentProgram={activeProgram?.title || undefined}
      pinLocked={pinLocked}
      contentKey={contentKey}
      contentId={contentId}
      activeView={activeView}
      safeToShow={safeToShow}
      title={title}
      contentTitle={name}
      subtitle={subtitle}
      logo={logoUrl}
      poster={PlayerEmptyPoster}
      className={className}
      onCloseClick={onCloseClick}
      onProgressReachedEnd={onProgressReachedEnd}
      onProgramClick={onProgramClick}
      onRedirectTvClick={noop}
      onPlayNext={isChannelsFilled ? onPlayNext : undefined}
      onPlayPrevious={isChannelsFilled ? onPlayPrevious : undefined}
      onPlaying={onPlaying}
      onTokenExpired={onTokenExpired}
      npawData={npawData}
      muted
      /* All rewind values are in sec */
      liveProgressBar={liveProgressBar}
      isRewound={isRewound || isRewindDelayed}
      onLiveRewind={onLiveRewind}
      onDelayedLiveRewind={onDelayedLiveRewind}
      onGoLive={onGoLive}
      isRewindPending={isRewindPending}
      rewindDetails={rewindDetails}
      isContentPaid={isPaid(selectedEpgChannel)}
      dolbyAtmosAvailable={selectedEpgChannel?.dolbyAtmosAvailable}
    >
      {playerChildren}
    </PlayerWrap>
  )
}
